import React, { useState } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Settings() {
  const { user, token, logout } = useAuth();
  const nav = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const deleteAccount = async () => {
    if (confirm !== "DELETE") return toast.error("Type DELETE to confirm.");
    setBusy(true);
    try {
      await fetch("/api/auth/delete-account", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Account deleted.");
      await logout();
      nav("/");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display">Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your account preferences.</p>
      </div>

      {/* Account info */}
      <div className="card-base p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Account</h2>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Name</span>
          <span className="font-medium">{user?.name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Phone</span>
          <span className="font-medium">{user?.phone}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Role</span>
          <span className="font-medium capitalize">{user?.role?.replace("_", " ")}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">KYC Status</span>
          <span className={`font-medium ${user?.kyc_status === "verified" ? "text-emerald-600" : "text-amber-600"}`}>
            {user?.kyc_status === "verified" ? "Verified" : "Pending"}
          </span>
        </div>
      </div>

      {/* Legal */}
      <div className="card-base p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Legal</h2>
        <div className="space-y-2">
          <a href="/terms" target="_blank" className="block text-sm text-[#F97316] hover:underline">Terms & Conditions →</a>
          <a href="/privacy" target="_blank" className="block text-sm text-[#F97316] hover:underline">Privacy Policy →</a>
        </div>
      </div>

      {/* Danger zone */}
      <div className="card-base p-5 space-y-3 border-red-200">
        <h2 className="text-sm font-semibold text-red-600">Danger Zone</h2>
        <p className="text-sm text-gray-500">Deleting your account removes all personal data. Signed agreements and financial records are retained as required by law.</p>
        {!showDelete ? (
          <button onClick={() => setShowDelete(true)} className="text-sm text-red-600 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-50">
            Delete my account
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-red-700">Type DELETE to confirm. This cannot be undone.</p>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type DELETE"
              className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-100"
            />
            <div className="flex gap-2">
              <button onClick={deleteAccount} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {busy ? "Deleting…" : "Confirm Delete"}
              </button>
              <button onClick={() => { setShowDelete(false); setConfirm(""); }} className="border border-gray-300 px-4 py-2 rounded-lg text-sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function UpiSection({ user, token }) {
  const [upi, setUpi] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [balance, setBalance] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    fetch("/api/wallet", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setBalance(d.balance_paise || 0); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [token]);

  const withdraw = async () => {
    if (!upi || !upi.includes("@")) {
      toast.error("Enter a valid UPI ID like name@upi");
      return;
    }
    const paise = Math.round(Number(amount) * 100);
    if (!paise || paise < 25000) {
      toast.error("Minimum withdrawal is ₹250");
      return;
    }
    if (paise > balance) {
      toast.error("Amount exceeds available balance");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/wallet/withdraw", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount_paise: paise, upi_id: upi }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Withdrawal failed"); return; }
      toast.success(`₹${Number(amount).toLocaleString("en-IN")} sent to ${upi}`);
      setBalance(b => b - paise);
      setAmount("");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const fmt = (p) => `₹${(p / 100).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4">
      {loaded && balance !== null && (
        <div className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
          <span className="text-sm text-gray-600">Available balance</span>
          <span className="font-bold text-[#F97316] text-lg">{fmt(balance)}</span>
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Your UPI ID</label>
        <input
          value={upi}
          onChange={e => setUpi(e.target.value)}
          placeholder="yourname@upi"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F97316]"
        />
        <p className="text-xs text-gray-400 mt-1">e.g. name@okicici, name@paytm, name@ybl</p>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Amount to withdraw (₹)</label>
        <input
          type="number"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="Minimum ₹250"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F97316]"
        />
      </div>
      {user?.kyc_status !== "verified" && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ Complete KYC verification before withdrawing
        </div>
      )}
      <button
        onClick={withdraw}
        disabled={busy || user?.kyc_status !== "verified"}
        className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? "Processing..." : "Withdraw to UPI"}
      </button>
      <p className="text-xs text-gray-400 text-center">
        Payouts processed within 1-2 business days via RazorpayX
      </p>
    </div>
  );
}
